import { Copy } from 'lucide-react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import {
  useGetBrokersMembersMemberIdCardsCardIdCardholderDetails,
} from 'utils/web_api'
import { type BrokersCard } from 'utils/web_api.schemas'

const formatExpirationDate = (expirationDate: string): string => {
  // expiration_date format is "2027-12", convert to "12/2027"
  const parts = expirationDate.split('-')
  if (parts.length === 2) {
    return `${parts[1]}/${parts[0]}`
  }
  return expirationDate
}

type CardholderDetailsSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberId: string
  card: BrokersCard | null
}

export const CardholderDetailsSheet = ({
  open,
  onOpenChange,
  memberId,
  card,
}: CardholderDetailsSheetProps) => {
  const { data: cardholderDetails, isLoading, isError, error } =
    useGetBrokersMembersMemberIdCardsCardIdCardholderDetails(
      memberId,
      card?.id ?? '',
      {
        query: {
          enabled: open && !!card?.id,
        },
      }
    )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='h-screen flex flex-col' style={{ minWidth: '720px' }}>
        <div className='border-b border-gray-200 pb-2'>
          <SheetHeader>
            <SheetTitle className='text-left'>
              <div className='text-2xl font-normal mb-2'>Cardholder Details</div>
            </SheetTitle>
            <SheetDescription className='text-left mt-4 mb-6'>
              These are the details for the cardholder that the bank has on file. If the member provides details to the merchant that don't match what is below, the card will be declined.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className='flex-1 overflow-y-auto pt-1'>
          {isLoading && (
            <div className='py-4'>
              <p className='text-gray-600'>Loading cardholder details...</p>
            </div>
          )}
          {isError && (
            <div className='py-4'>
              <p className='text-red-600'>
                {(error as GrapeAPIErrorType)?.errors?.[0] ?? 'Failed to load cardholder details'}
              </p>
            </div>
          )}
          {!isLoading && !isError && cardholderDetails && card && (
            <div className='space-y-2 pb-4'>
              {/* Cardholder Details Section */}
              <div className='mb-6'>
                <h3 className='text-lg font-semibold mb-4'>Cardholder</h3>
                <div className='space-y-1 text-sm'>
                  {cardholderDetails.cardholder_name && (
                    <div className='flex items-center gap-2'>
                      <span className='text-gray-600'>Full name:</span>
                      <span className='font-light'>{cardholderDetails.cardholder_name}</span>
                      <CopyButtonWithAnimation content={cardholderDetails.cardholder_name}>
                        <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                      </CopyButtonWithAnimation>
                    </div>
                  )}
                  {cardholderDetails.address && (
                    <>
                      {cardholderDetails.address.street && typeof cardholderDetails.address.street === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>Address Line 1:</span>
                          <span className='font-light'>{cardholderDetails.address.street}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.street}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                      {cardholderDetails.address.street2 && typeof cardholderDetails.address.street2 === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>Address Line 2:</span>
                          <span className='font-light'>{cardholderDetails.address.street2}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.street2}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                      {cardholderDetails.address.city && typeof cardholderDetails.address.city === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>City:</span>
                          <span className='font-light'>{cardholderDetails.address.city}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.city}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                      {cardholderDetails.address.state && typeof cardholderDetails.address.state === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>State:</span>
                          <span className='font-light'>{cardholderDetails.address.state}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.state}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                      {cardholderDetails.address.postal_code && typeof cardholderDetails.address.postal_code === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>Postal Code:</span>
                          <span className='font-light'>{cardholderDetails.address.postal_code}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.postal_code}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                      {cardholderDetails.address.country && typeof cardholderDetails.address.country === 'string' && (
                        <div className='flex items-center gap-2'>
                          <span className='text-gray-600'>Country:</span>
                          <span className='font-light'>{cardholderDetails.address.country}</span>
                          <CopyButtonWithAnimation content={cardholderDetails.address.country}>
                            <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                          </CopyButtonWithAnimation>
                        </div>
                      )}
                    </>
                  )}
                  {cardholderDetails.phone_number && (
                    <div className='flex items-center gap-2'>
                      <span className='text-gray-600'>Phone number:</span>
                      <span className='font-light'>{cardholderDetails.phone_number}</span>
                      <CopyButtonWithAnimation content={cardholderDetails.phone_number}>
                        <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                      </CopyButtonWithAnimation>
                    </div>
                  )}
                  {cardholderDetails.email && (
                    <div className='flex items-center gap-2'>
                      <span className='text-gray-600'>Email:</span>
                      <span className='font-light'>{cardholderDetails.email}</span>
                      <CopyButtonWithAnimation content={cardholderDetails.email}>
                        <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                      </CopyButtonWithAnimation>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Details Section */}
              <div>
                <h3 className='text-lg font-semibold mb-4'>Card</h3>
                <div className='space-y-1 text-sm'>
                  {card.expiration_date && (
                    <div className='flex items-center gap-2'>
                      <span className='text-gray-600'>Expiration Date:</span>
                      <span className='font-light'>{formatExpirationDate(card.expiration_date)}</span>
                      <CopyButtonWithAnimation content={formatExpirationDate(card.expiration_date)}>
                        <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                      </CopyButtonWithAnimation>
                    </div>
                  )}
                  {card.last4_digits && (
                    <div className='flex items-center gap-2'>
                      <span className='text-gray-600'>Last 4 Digits:</span>
                      <span className='font-light'>{card.last4_digits}</span>
                      <CopyButtonWithAnimation content={card.last4_digits}>
                        <Copy className='text-gray-500 hover:text-gray-700 transition-colors' size={16} />
                      </CopyButtonWithAnimation>
                    </div>
                  )}
                </div>
                <p className='text-sm text-gray-600 mt-4'>
                  Note: Only the member can view their card number. Please refer them to their dashboard if asked for card details.
                </p>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
